/**
 * FAQ System - Cross-section communication via custom events
 * Enables search and category filtering across multiple FAQ sections
 */

// Global FAQ state management
window.FAQSystem = window.FAQSystem || {
  currentCategory: 'all',
  currentSearchTerm: '',
  initialized: false
};

/**
 * FAQ Search Component
 * Dispatches search events to all FAQ item sections
 */
class FaqSearch extends HTMLElement {
  constructor() {
    super();
    this.DEBOUNCE_DELAY = 300;
    this.debounceTimer = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (window.wetheme && window.wetheme.webcomponentRegistry) {
      window.wetheme.webcomponentRegistry.register({ key: 'faq-search' });
    }

    this.searchInput = this.querySelector('[data-faq-search-input]');
    this.searchIcon = this.querySelector('.faq-search__icon');

    if (this.searchInput) {
      this.bindEvents();
      // Restore search state if exists
      if (window.FAQSystem.currentSearchTerm) {
        this.searchInput.value = window.FAQSystem.currentSearchTerm;
        this.updateSearchIcon();
      }
    }
  }

  bindEvents() {
    this.searchInput.addEventListener('input', (e) => {
      this.updateSearchIcon();
      this.debounce(() => {
        this.handleSearch(e.target.value);
      }, this.DEBOUNCE_DELAY);
    });

    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.searchInput.value = '';
        this.handleSearch('');
        this.updateSearchIcon();
        this.searchInput.blur();
      }
    });
  }

  updateSearchIcon() {
    if (this.searchIcon) {
      this.searchIcon.style.opacity = this.searchInput.value.length > 0 ? '0' : '1';
    }
  }

  handleSearch(searchTerm) {
    const normalizedTerm = searchTerm.toLowerCase().trim();
    window.FAQSystem.currentSearchTerm = normalizedTerm;

    // Dispatch custom event for all FAQ item sections
    document.dispatchEvent(new CustomEvent('faq:search', {
      detail: { searchTerm: normalizedTerm }
    }));
  }

  debounce(callback, delay) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(callback, delay);
  }
}

/**
 * FAQ Categories Component
 * Manages category filter buttons and dispatches category change events
 */
class FaqCategories extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  init() {
    if (window.wetheme && window.wetheme.webcomponentRegistry) {
      window.wetheme.webcomponentRegistry.register({ key: 'faq-categories' });
    }

    this.categoryButtons = this.querySelectorAll('[data-faq-category]');
    this.bindEvents();

    // Restore category state if exists
    if (window.FAQSystem.currentCategory !== 'all') {
      this.setActiveCategory(window.FAQSystem.currentCategory);
    }
  }

  bindEvents() {
    this.categoryButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleCategoryClick(button);
      });

      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleCategoryClick(button);
        }
      });
    });
  }

  handleCategoryClick(clickedButton) {
    const category = clickedButton.dataset.faqCategory;
    this.setActiveCategory(category);

    window.FAQSystem.currentCategory = category;

    // Dispatch custom event for all FAQ item sections
    document.dispatchEvent(new CustomEvent('faq:category-change', {
      detail: { category }
    }));
  }

  setActiveCategory(category) {
    this.categoryButtons.forEach(btn => {
      const isActive = btn.dataset.faqCategory === category;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }
}

/**
 * FAQ Items Component
 * Listens for search and category events, filters items accordingly
 */
class FaqItems extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  init() {
    if (window.wetheme && window.wetheme.webcomponentRegistry) {
      window.wetheme.webcomponentRegistry.register({ key: 'faq-items' });
    }

    this.categoryHandle = this.dataset.categoryHandle || '';
    this.faqItems = this.querySelectorAll('[data-faq-item]');
    this.noResultsMessage = this.querySelector('[data-faq-no-results]');
    this.sectionWrapper = this.closest('.faq-items-section');

    this.bindEvents();

    // Apply initial state
    this.filterItems();
  }

  bindEvents() {
    document.addEventListener('faq:search', (e) => {
      this.filterItems();
    });

    document.addEventListener('faq:category-change', (e) => {
      this.filterItems();
    });
  }

  filterItems() {
    const currentCategory = window.FAQSystem.currentCategory;
    const currentSearchTerm = window.FAQSystem.currentSearchTerm;

    // Check if this section's category matches
    const sectionMatchesCategory = currentCategory === 'all' || this.categoryHandle === currentCategory;

    // Hide entire section if category doesn't match
    if (this.sectionWrapper) {
      if (sectionMatchesCategory) {
        this.sectionWrapper.classList.remove('faq-section--hidden');
        this.sectionWrapper.style.display = '';
      } else {
        this.sectionWrapper.classList.add('faq-section--hidden');
        setTimeout(() => {
          if (this.sectionWrapper.classList.contains('faq-section--hidden')) {
            this.sectionWrapper.style.display = 'none';
          }
        }, 250);
        return; // Skip item filtering if section is hidden
      }
    }

    // Filter individual items within this section
    let visibleCount = 0;

    this.faqItems.forEach(item => {
      const itemQuestion = (item.dataset.faqItemQuestion || '').toLowerCase();
      const itemAnswer = (item.dataset.faqItemAnswer || '').toLowerCase();

      // Check search match
      const searchMatch = currentSearchTerm === '' ||
        itemQuestion.includes(currentSearchTerm) ||
        itemAnswer.includes(currentSearchTerm);

      if (searchMatch) {
        item.classList.remove('faq-item--hidden');
        item.style.display = '';
        visibleCount++;
      } else {
        item.classList.add('faq-item--hidden');
        setTimeout(() => {
          if (item.classList.contains('faq-item--hidden')) {
            item.style.display = 'none';
          }
        }, 250);
      }
    });

    // Show/hide no results message
    if (this.noResultsMessage) {
      if (visibleCount === 0 && sectionMatchesCategory) {
        this.noResultsMessage.classList.remove('hidden');
      } else {
        this.noResultsMessage.classList.add('hidden');
      }
    }

    // Dispatch event with visibility info for global no-results handling
    document.dispatchEvent(new CustomEvent('faq:items-filtered', {
      detail: {
        sectionHandle: this.categoryHandle,
        visibleCount,
        sectionVisible: sectionMatchesCategory
      }
    }));
  }
}

/**
 * FAQ No Results Handler
 * Shows global no results message when all sections have no visible items
 */
class FaqNoResults extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  init() {
    this.sectionResults = new Map();

    document.addEventListener('faq:items-filtered', (e) => {
      const { sectionHandle, visibleCount, sectionVisible } = e.detail;

      if (sectionVisible) {
        this.sectionResults.set(sectionHandle, visibleCount);
      } else {
        this.sectionResults.delete(sectionHandle);
      }

      this.updateVisibility();
    });

    // Also listen for search changes to reset
    document.addEventListener('faq:search', () => {
      this.sectionResults.clear();
    });

    document.addEventListener('faq:category-change', () => {
      this.sectionResults.clear();
    });
  }

  updateVisibility() {
    // Use requestAnimationFrame to wait for all sections to report
    requestAnimationFrame(() => {
      let totalVisible = 0;
      this.sectionResults.forEach(count => {
        totalVisible += count;
      });

      if (totalVisible === 0 && this.sectionResults.size > 0) {
        this.classList.remove('hidden');
      } else {
        this.classList.add('hidden');
      }
    });
  }
}

// Register custom elements
if (!customElements.get('faq-search')) {
  customElements.define('faq-search', FaqSearch);
}

if (!customElements.get('faq-categories')) {
  customElements.define('faq-categories', FaqCategories);
}

if (!customElements.get('faq-items')) {
  customElements.define('faq-items', FaqItems);
}

if (!customElements.get('faq-no-results')) {
  customElements.define('faq-no-results', FaqNoResults);
}
